<template>
    <div class="dns-diagram-container">
        <div class="dns-flow-wrapper">
            <vue-flow 
                v-model="elements" 
                :default-zoom="0.8" 
                :min-zoom="0.8" 
                :max-zoom="0.8"
                :fit-view-on-init="true" 
                @pane-ready="onReady"
                :default-edge-options="defaultEdgeOptions"
                :nodes-draggable="false"
                :pan-on-drag="false"
                :zoom-on-scroll="false"
                :zoom-on-pinch="false"
                :zoom-on-double-click="false"
                :pan-on-scroll="false"
                :selection-key-code="null"
                :multi-selection-key-code="null"
                :delete-key-code="null"
                class="dns-flow"
            >
                <template #node-website="nodeProps">
                    <div class="dns-node website-node">
                        <div class="node-icon">🌐</div>
                        <div class="node-content">
                            <div class="website-url">{{ nodeProps.data.label }}</div>
                            <div class="website-divider"></div>
                            <div class="tag-info">{{ nodeProps.data.tagInfo }}</div>
                        </div>
                    </div>
                </template>

                <template #node-dns="nodeProps">
                    <div class="dns-node dns-record-node">
                        <div class="node-icon">🔗</div>
                        <div class="node-content">
                            <div class="node-title">{{ nodeProps.data.recordType }}</div>
                            <div class="node-value">{{ nodeProps.data.label }}</div>
                        </div>
                    </div>
                </template>

                <template #node-akamai="nodeProps">
                    <div class="dns-node akamai-node">
                        <div class="node-icon edge-server-icon">☁️</div>
                        <div class="akamai-content">
                            <div class="akamai-logo-small">
                                <img :src="akamaiLogo" alt="Akamai" class="akamai-image-small" />
                            </div>
                            <div class="akamai-title">{{ nodeProps.data.title }}</div>
                            <div class="akamai-hostname">{{ nodeProps.data.label }}</div>
                        </div>
                    </div>
                </template>

                <template #node-cloud="nodeProps">
                    <div class="dns-node cloud-node">
                        <div class="node-icon">☁️</div>
                        <div class="node-content">
                            <div class="node-title">{{ nodeProps.data.title }}</div>
                            <div class="node-value">{{ nodeProps.data.label }}</div>
                        </div>
                    </div>
                </template>

                <template #node-section="nodeProps">
                    <div class="section-header">
                        <h5>{{ nodeProps.data.label }}</h5>
                    </div>
                </template>


                <Background pattern-color="#f0f0f0" gap="20" />
            </vue-flow>
        </div>
        
        <div class="dns-legend">
            <div class="legend-item">
                <div class="legend-icon">🌐</div>
                <span>Website/Domain</span>
            </div>
            <div class="legend-item">
                <div class="legend-icon">🔗</div>
                <span>DNS Record</span>
            </div>
            <div class="legend-item">
                <div class="legend-icon">☁️</div>
                <span>Edge Server</span>
            </div>
        </div>
    </div>
</template>

<script>
import { VueFlow, Background, useVueFlow } from '@vue-flow/core';
import { ref, computed, watch } from 'vue';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import akamaiLogo from '@/assets/akamai.png';

export default {
    name: 'DNSDiagram',
    components: {
        VueFlow,
        Background
    },
    props: {
        dnsMethod: {
            type: String,
            default: 'chaining'
        },
        domainName: {
            type: String,
            default: 'example.com'
        },
        tagSubdomain: {
            type: String,
            default: 'tag'
        },
        edgeHostname: {
            type: String,
            default: 'e1.a00000-c999.edgekey.net'
        }
    },
    setup(props) {
        const { fitView } = useVueFlow();
        const elements = ref([]);
        
        // Computed properties for common values
        const websiteUrl = computed(() => `${props.tagSubdomain}.${props.domainName}`);
        const proxyUrl = computed(() => `${props.tagSubdomain}1.${props.domainName}`);
        const cloudFrontUrl = 'abc123-example.cloudfront.net';
        
        const defaultEdgeOptions = {
            type: 'smoothstep',
            animated: true,
            style: {
                stroke: '#3b82f6',
                strokeWidth: 2,
            },
            markerEnd: {
                type: 'arrow',
                width: 20,
                height: 20,
            },
        };

        // Helper functions for common node and edge properties
        const createNode = (id, type, position, data, additionalProps = {}) => ({
            id,
            type,
            position,
            data,
            draggable: false,
            selectable: false,
            ...additionalProps
        });

        const createEdge = (id, source, target, style = {}, additionalProps = {}) => ({
            id,
            source,
            target,
            style: { stroke: '#e91e63', strokeWidth: 2, ...style },
            selectable: false,
            markerEnd: {
                type: 'arrow',
                width: 20,
                height: 20,
                color: style.stroke || '#e91e63',
            },
            ...additionalProps
        });

        const onReady = () => {
            setTimeout(() => {
                fitView({ padding: 0.1 });
            }, 100);
        };

        const createChainingDiagram = () => {
            return [
                // Section Headers
                createNode('web-section', 'section', { x: 105, y: 20 }, { label: 'Web Site' }),
                createNode('dns-section', 'section', { x: 400, y: 20 }, { label: 'DNS' }),
                createNode('app-section', 'section', { x: 720, y: 20 }, { label: 'Application' }),
                
                // Main Flow
                createNode('website', 'website', { x: 50, y: 120 }, { 
                    label: `https://${props.domainName}`,
                    tagInfo: `aUT Tag\ndmn = ${websiteUrl.value}`
                }),
                createNode('cname', 'dns', { x: 300, y: 80 }, { 
                    label: `${websiteUrl.value}\n→\n${proxyUrl.value}`,
                    recordType: 'CNAME'
                }),
                createNode('aname', 'dns', { x: 300, y: 180 }, { 
                    label: `${proxyUrl.value}\n→\n${props.edgeHostname}`,
                    recordType: 'ANAME'
                }),
                createNode('akamai', 'akamai', { x: 600, y: 130 }, { 
                    label: props.edgeHostname,
                    title: 'aUT Edge Server'
                }),
                
                // Edges
                createEdge('e1', 'website', 'cname'),
                createEdge('e2', 'cname', 'aname'),
                createEdge('e3', 'aname', 'akamai')
            ];
        };

        const createCnameOnlyDiagram = () => {
            return [
                // Section Headers
                createNode('web-section', 'section', { x: 105, y: 20 }, { label: 'Web Site' }),
                createNode('dns-section', 'section', { x: 400, y: 20 }, { label: 'DNS' }),
                createNode('app-section', 'section', { x: 720, y: 20 }, { label: 'Application' }),
                
                // Main Flow
                createNode('website', 'website', { x: 0, y: 120 }, { 
                    label: `https://${props.domainName}`,
                    tagInfo: `aUT Tag\ndmn = ${websiteUrl.value}`
                }),
                createNode('cname', 'dns', { x: 300, y: 120 }, { 
                    label: `${websiteUrl.value}\n→\n${props.edgeHostname}`,
                    recordType: 'CNAME'
                }),
                createNode('akamai', 'akamai', { x: 600, y: 120 }, { 
                    label: props.edgeHostname,
                    title: 'aUT Edge Server'
                }),
                
                // Edges
                createEdge('e1', 'website', 'cname'),
                createEdge('e2', 'cname', 'akamai')
            ];
        };

        const createAwsRoute53Diagram = () => {
            return [
                // Section Headers
                createNode('web-section', 'section', { x: 105, y: 20 }, { label: 'Web Site' }),
                createNode('dns-section', 'section', { x: 365, y: 20 }, { label: 'DNS' }),
                createNode('proxy-section', 'section', { x: 365, y: 260 }, { label: 'Reverse Proxy' }),
                createNode('app-section', 'section', { x: 700, y: 20 }, { label: 'Application' }),
                
                // Main Flow
                createNode('website', 'website', { x: -50, y: 120 }, { 
                    label: `https://${props.domainName}`,
                    tagInfo: `aUT Tag\ndmn = ${websiteUrl.value}`
                }),
                createNode('route53', 'cloud', { x: 250, y: 80 }, { 
                    label: `CNAME - ${websiteUrl.value}\n→ ${proxyUrl.value}\n\n${proxyUrl.value}\n→ ${cloudFrontUrl}`,
                    title: 'Route 53'
                }),
                createNode('cloudfront', 'cloud', { x: 290, y: 300 }, { 
                    label: `CloudFront Distribution Name\n${cloudFrontUrl}\n\nAlternate Domain Name\n${websiteUrl.value}`,
                    title: 'CloudFront'
                }),
                createNode('akamai', 'akamai', { x: 580, y: 120 }, { 
                    label: props.edgeHostname,
                    title: 'aUT Edge Server'
                }),
                
                // Complex edges with custom styling
                createEdge('e1', 'website', 'route53', { transform: 'translateX(-20px)' }, { type: 'smoothstep' }),
                createEdge('e2', 'route53', 'cloudfront', { strokeDasharray: '5 5', transform: 'translateX(-50px)' }, { 
                    type: 'smoothstep' 
                }),
                createEdge('e3', 'cloudfront', 'route53', { 
                    stroke: '#2669c3', 
                    strokeDasharray: '5 5', 
                    transform: 'translateX(0px)' 
                }, { 
                    type: 'smoothstep',
                    markerStart: { width: 10, height: 10, color: '#2669c3' }
                }),
                createEdge('e4', 'route53', 'akamai', { transform: 'translateX(40px)' }, { type: 'smoothstep' })
            ];
        };

        const createCloudFlareDnsDiagram = () => {
            return [
                // Section Headers
                createNode('web-section', 'section', { x: 105, y: 20 }, { label: 'Web Site' }),
                createNode('dns-section', 'section', { x: 400, y: 20 }, { label: 'DNS' }),
                createNode('app-section', 'section', { x: 750, y: 20 }, { label: 'Application' }),
                
                // Main Flow
                createNode('website', 'website', { x: 0, y: 120 }, { 
                    label: `https://${props.domainName}`,
                    tagInfo: `aUT Tag\ndmn = ${websiteUrl.value}`
                }),
                createNode('cloudflare-dns', 'cloud', { x: 220, y: 80 }, { 
                    label: `CNAME \n${websiteUrl.value} -> ${proxyUrl.value}\nProxy: Disabled`,
                    title: 'Cloudflare DNS'
                }),
                createNode('ns-records', 'cloud', { x: 220, y: 220 }, { 
                    label: `${proxyUrl.value} \n\nns-xxx.xxxx.com\nns-xxx.xxxx.com\nns-xxx.xxxx.com\nns-xxx.xxxx.com`,
                    title: 'Name Server'
                }),
                createNode('route53-zone', 'cloud', { x: 480, y: 150 }, { 
                    label: `${proxyUrl.value} -> \n${cloudFrontUrl}`,
                    title: 'Route53 Alias'
                }),
                createNode('akamai', 'akamai', { x: 720, y: 120 }, { 
                    label: props.edgeHostname,
                    title: 'aUT Edge Server'
                }),
                
                // Edges
                createEdge('e1', 'website', 'cloudflare-dns'),
                createEdge('e2', 'cloudflare-dns', 'ns-records'),
                createEdge('e3', 'ns-records', 'route53-zone'),
                createEdge('e4', 'route53-zone', 'akamai')
            ];
        };

        const createCloudFlareHostedDiagram = () => {
            return [
                // Section Headers
                createNode('web-section', 'section', { x: 105, y: 20 }, { label: 'Web Site' }),
                createNode('dns-section', 'section', { x: 400, y: 20 }, { label: 'DNS & Hosting' }),
                createNode('app-section', 'section', { x: 750, y: 20 }, { label: 'Application' }),
                
                // Main Flow
                createNode('website', 'website', { x: 0, y: 120 }, { 
                    label: `https://${props.domainName}`,
                    tagInfo: `aUT Tag\ndmn = ${websiteUrl.value}`
                }),
                createNode('cloudflare-hosted', 'cloud', { x: 280, y: 80 }, { 
                    label: `HTML Page loads tags from -\n${websiteUrl.value}\n\nPAGE RULE\nPixel Redirect\n${proxyUrl.value}`,
                    title: 'CloudFlare'
                }),
                createNode('akamai-redirect', 'cloud', { x: 350, y: 280 }, { 
                    label: `aUT Edge server\n${props.edgeHostname}`,
                    title: 'Redirect Target'
                }),
                createNode('akamai', 'akamai', { x: 650, y: 120 }, { 
                    label: props.edgeHostname,
                    title: 'aUT Edge Server'
                }),
                
                // Edges with bidirectional arrows
                createEdge('e1', 'website', 'cloudflare-hosted', {}, { 
                    markerStart: { type: 'arrow', color: '#e91e63', width: 20, height: 20 }
                }),
                createEdge('e2', 'cloudflare-hosted', 'akamai-redirect', {}, { 
                    markerStart: { type: 'arrow', color: '#e91e63', width: 20, height: 20 }
                }),
                createEdge('e3', 'akamai-redirect', 'akamai', {}, { 
                    markerStart: { type: 'arrow', color: '#e91e63', width: 20, height: 20 }
                })
            ];
        };

        const generateDiagram = computed(() => {
            switch (props.dnsMethod) {
                case 'chaining':
                    return createChainingDiagram();
                case 'cname-only':
                    return createCnameOnlyDiagram();
                case 'aws-route53':
                    return createAwsRoute53Diagram();
                case 'cloudflare-dns':
                    return createCloudFlareDnsDiagram();
                case 'cloudflare-hosted':
                    return createCloudFlareHostedDiagram();
                default:
                    return createChainingDiagram();
            }
        });

        watch(generateDiagram, (newDiagram) => {
            elements.value = newDiagram;
            setTimeout(() => {
                fitView({ padding: 0.1 });
            }, 100);
        }, { immediate: true });

        return {
            elements,
            defaultEdgeOptions,
            onReady,
            akamaiLogo
        };
    }
};
</script>

<style scoped>
.dns-diagram-container {
    width: 100%;
    height: 400px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #f9fafb;
    overflow: hidden;
    position: relative;
}

.dns-flow-wrapper {
    width: 100%;
    height: calc(100% - 40px);
}

.dns-flow {
    width: 100%;
    height: 100%;
}

.dns-node {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border-radius: 8px;
    border: 2px solid;
    background: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    min-width: 180px;
    max-width: 220px;
}

.website-node {
    border-color: #3b82f6;
    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
    min-width: 160px;
    max-width: 180px;
    min-height: 90px;
    padding: 12px;
    font-size: 11px;
}

.website-node .node-content {
    flex: 1;
}

.website-url {
    font-weight: bold;
    font-size: 12px;
    margin-bottom: 12px;
    color: black;
}

.website-divider {
    height: 1px;
    background: #3b82f6;
    margin: 12px 0;
}

.tag-info {
    font-size: 10px;
    color: #374151;
    white-space: pre-line;
    margin-top: 8px;
}

.dns-record-node {
    border-color: #10b981;
    background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
}

.akamai-node {
    border-color: #f59e0b;
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    min-width: 170px;
    max-width: 200px;
    font-size: 10px;
}

.edge-server-icon {
    font-size: 24px !important;
}

.akamai-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex: 1;
}

.akamai-logo-small {
    margin-bottom: 4px;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 6px;
    padding: 4px;
    display: inline-block;
}

.akamai-image-small {
    width: 70px !important;
    height: auto !important;
    max-height: 45px;
    mix-blend-mode: multiply;
    background: transparent;
    filter: contrast(1.1) saturate(1.2);
}

.akamai-title {
    font-size: 10px;
    font-weight: normal;
    color: #374151;
    margin-bottom: 4px;
}

.akamai-hostname {
    font-size: 9px;
    color: #6b7280;
    word-break: break-all;
}

.cloud-node {
    border-color: #8b5cf6;
    background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
    min-height: 90px;
    max-width: 220px;
    padding: 8px;
    white-space: pre-line;
    word-break: keep-all;
    font-size: 10px;
}

.section-header {
    background: none;
    border: none;
    text-align: center;
    padding: 8px 0;
    font-weight: bold;
    color: #374151;
    transform: translateX(-50%);
    white-space: nowrap;
}

.section-header h5 {
    margin: 0;
    font-size: 14px;
    color: #6b7280;
    font-weight: 600;
}


.node-icon {
    font-size: 20px;
    margin-right: 12px;
    flex-shrink: 0;
}

.node-content {
    flex: 1;
    min-width: 0;
}

.node-title {
    font-weight: 600;
    font-size: 12px;
    color: #374151;
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.node-value {
    font-size: 11px;
    color: #6b7280;
    word-break: break-all;
    line-height: 1.3;
}

.dns-legend {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 40px;
    background: rgba(255, 255, 255, 0.95);
    border-top: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;
    padding: 0 16px;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #6b7280;
}

.legend-icon {
    font-size: 14px;
}

/* Vue Flow custom styles */
:deep(.vue-flow__edge-path) {
    stroke: #3b82f6;
    stroke-width: 2;
}

:deep(.vue-flow__edge-textbg) {
    fill: white;
}

:deep(.vue-flow__edge-text) {
    font-size: 10px;
    fill: #374151;
    font-weight: 500;
}

:deep(.vue-flow__controls) {
    bottom: 45px;
}

:deep(.vue-flow__background) {
    background-color: #f9fafb;
}
</style>

<style lang="scss">
@use '../theme/scss/_user-variables.scss' as *;

body.dark-mode {
    .dns-diagram-container {
        border: 1px solid $dark-border-color;
        background: $dark-card-bg;
    }
    .dns-legend {
        background: $dark-body-bg !important;
        border-top: 1px solid $dark-border-color;
    }
    .legend-item {
        background-color: $dark-body-bg !important;
    }
}
</style>
